import * as React from 'react';

import { useEvent } from 'react-use';

import { WINDOW_EVENTS } from '@/lib/constants/post-message-types';
import { HOLD_FOCUS_PARAM } from '@/lib/constants/widget-events';

interface UseComposerFocusHoldOptions {
  /** Called when the visitor opens the chat themselves. */
  onOpenedWithFocus: () => void;
}

interface ComposerFocusHold {
  isFocusHeld: boolean;
  /** Current even inside the event that changed it, before the re-render. */
  isFocusHeldRef: React.RefObject<boolean>;
}

function isHoldRequestedAtBoot(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(HOLD_FOCUS_PARAM) === '1';
}

/** One-shot, like the new-conversation param: a reload (a locale change) must not hold again. */
function clearHoldRequest(): void {
  if (!isHoldRequestedAtBoot()) return;
  const url = new URL(window.location.href);
  url.searchParams.delete(HOLD_FOCUS_PARAM);
  window.history.replaceState(window.history.state, '', url);
}

/**
 * While the chat is open without the visitor asking, nothing in it takes focus: moving the keyboard
 * into it would be an unexpected change of context (WCAG 3.2.5). Released by a click or key press
 * in the chat, or by the visitor opening it themselves.
 */
export function useComposerFocusHold({
  onOpenedWithFocus,
}: UseComposerFocusHoldOptions): ComposerFocusHold {
  const [isFocusHeld, setIsFocusHeld] = React.useState(isHoldRequestedAtBoot);
  const isFocusHeldRef = React.useRef(isFocusHeld);

  React.useEffect(clearHoldRequest, []);

  const setHold = React.useCallback(function setHold(isHeld: boolean) {
    isFocusHeldRef.current = isHeld;
    setIsFocusHeld(isHeld);
  }, []);

  const release = React.useCallback(
    function release() {
      if (isFocusHeldRef.current) setHold(false);
    },
    [setHold],
  );

  useEvent('pointerdown', release);
  useEvent('keydown', release);

  useEvent(WINDOW_EVENTS.OPENED, function applyOpenFocus(event: Event) {
    if ((event as CustomEvent<{ focus?: boolean }>).detail?.focus === false) {
      setHold(true);
      return;
    }
    setHold(false);
    onOpenedWithFocus();
  });

  return { isFocusHeld, isFocusHeldRef };
}
